import { ChangeEventHandler } from 'react';
import { AttributeType } from 'models/AttributeType';
import { AddItemRequest } from 'models/AddItemRequest';
import { AddItemResponse } from 'models/AddItemResponse';
import { rm } from 'utils';
import { getBrandWidget, splitTitle } from 'components/Shared/Product/selector';
import { AccordionContainer, AccordionSection } from 'components/Accordion';
import { ImageAttributeProvider } from 'views/ProductPage/configurators/imageAttributeContext';
import { MobileProductInfoHeader } from 'views/ProductPage/themes/BGH/productInfoHeader';
import { PureProductPrice as ProductPrice } from 'views/ProductPage/themes/BGH/productInfoHeader/price';
import { _VariantSelector as VariantSelector } from 'views/ProductPage/themes/BGH/sections/variantSelector';
import { _ProductMedia as ProductMedia } from 'views/ProductPage/productMedia';
import { _ProductShortDescription as ProductShortDescription } from 'views/ProductPage/themes/BGH/sections/productShortDescription';
import { _ProductDescription as ProductDescription } from 'views/ProductPage/themes/BGH/sections/productDescription';
import { ProductAttributes } from 'views/ProductPage/themes/BGH/sections/productAttributes';
import { ToggleAddon, _Addons as Addons } from 'views/ProductPage/addons';
import { Configurator, Installation } from 'views/ProductPage/configurators';
import { Section } from 'components/Section';
import { Divider } from 'views/ProductPage/themes/BGH/divider';
import {
  getHasShortDescription,
  getHasDescription,
  getHasAttributes,
  getHasReviews
} from 'views/ProductPage/themes/BGH/validators';
import productPageStyles from 'views/ProductPage/themes/BGH/styles.scss';
import { _AddToCartSection as AddToCartSection } from 'views/ProductPage/themes/BGH/addToCartSection';
import { DrawerReviewStats } from 'views/ProductPage/reviews/themes/BGH/reviewStats';
import { _ProductReviews as ProductReviews } from 'views/ProductPage/reviews/themes/BGH';
import { isInstallationConfigurator } from 'views/ProductPage/configurators/selector';
import { SelectInstallationProductAction } from 'views/ProductPage/configurators/installation/actions';
import { getChannel, getShouldDisableBackOrders, isPurchasablePure } from '../selector';
import { OwnState } from './reducer';
import styles from './styles.scss';
import { useSelector } from 'react-redux';
import { ProductDetailedMeta } from 'models/ProductMeta';
import { ProductPageModel } from 'models/ProductPageModel';

interface Props {
  fullProductId: string;
  variantId: string | undefined;
  cdnUrl: string;
  data: ProductPageModel;
  state: OwnState;
  onVariant: (variantId: string) => void;
  onReset: () => void;
  onSelectValue: (id: string, value: string) => void;
  setZipCode: ChangeEventHandler<HTMLInputElement>;
  isInstallationSelected: () => boolean;
  setInstallation: (productId: string) => void;
  toggleInstallationAddon: (id: string) => void;
  resetInstallationAddons: () => void;
  resetInstallationConfigurator: () => void;
  toggleAddon: ToggleAddon;
  handleAdd: (request: AddItemRequest) => Promise<AddItemResponse>;
  loadMoreReviews: () => Promise<void>;
  upvoteReview: (id: string) => Promise<void>;
}

export const Product = ({
  fullProductId,
  variantId,
  cdnUrl,
  data,
  state,
  onVariant,
  onReset,
  onSelectValue,
  setZipCode,
  isInstallationSelected,
  setInstallation,
  toggleInstallationAddon,
  resetInstallationAddons,
  resetInstallationConfigurator,
  toggleAddon,
  handleAdd,
  loadMoreReviews,
  upvoteReview
}: Props) => {
  const { product } = data;
  const configurator = state.product?.configurator;

  const hasShortDescription = getHasShortDescription(product);
  const hasDescription = getHasDescription(product);
  const hasAttributes = getHasAttributes(product);
  const hasReviews = getHasReviews({ reviews: state.reviews });
  const channel = useSelector(getChannel);
  const shouldDisableBackOrders = useSelector(getShouldDisableBackOrders);

  const customOptions =
    configurator?.attributes.filter(
      attributeOption =>
        attributeOption.type !== AttributeType.Input &&
        attributeOption.type !== AttributeType.NumericInput &&
        !configurator?.values?.[attributeOption.id]
    ) || [];

  return (
    <>
      <Section>
        <ImageAttributeProvider>
          <>
            <MobileProductInfoHeader
              {...{
                brand: product.brandName,
                variantName: product.variantName,
                score: product.reviewSummary?.averageScore || 0,
                amountOfReviews: product.reviewSummary?.numberOfReviews || 0,
                ...splitTitle(product)
              }}
            />
            <ProductMedia
              cdnUrl={cdnUrl}
              configurator={configurator}
              widgets={product.widgets}
              hideOverlayWidgets
              isLargeImage
              className={productPageStyles.productMediaMobile}
              mediaClassNames={{
                thumbnails: styles.productMediaThumbnails,
                thumbnail: styles.productMediaThumbnail
              }}
            />
            {configurator && (
              <VariantSelector
                {...{
                  isPhone: true,
                  attributes: configurator.attributes,
                  values: configurator.values,
                  variantId: product.variantId,
                  className: productPageStyles.variantSelectorMobile,
                  isInstallation: isInstallationConfigurator(configurator),
                  accessoryConfigurator: (
                    <Configurator
                      {...{
                        isMobile: true,
                        inDrawer: true,
                        configurator: configurator,
                        isConfigurable: configurator.type !== null,
                        isFamily: product.isFamily,
                        addons: data.addons || {},
                        onVariant,
                        configReset: onReset,
                        reset: onReset,
                        onSelectValue
                      }}
                    />
                  ),
                  accessoryInstallation: isInstallationConfigurator(configurator) && (
                    <Installation
                      {...{
                        isMobile: true,
                        inDrawer: true,
                        configurator: configurator,
                        isConfigurable: configurator.type !== null,
                        isFamily: product.isFamily,
                        addons: data.addons || {},
                        onVariant,
                        configReset: onReset,
                        reset: onReset,
                        onSelectValue,
                        setZipCode,
                        zipCode: state.zipCode || '',
                        isInstallationSelected: isInstallationSelected(),
                        selectInstallation:
                          setInstallation as unknown as () => SelectInstallationProductAction,
                        selectAddon:
                          toggleInstallationAddon as unknown as () => SelectInstallationProductAction,
                        resetAddons:
                          resetInstallationAddons as unknown as () => SelectInstallationProductAction,
                        resetConfigurator:
                          resetInstallationConfigurator as unknown as () => SelectInstallationProductAction
                      }}
                    />
                  )
                }}
              />
            )}
            <Addons
              addons={state.addons || data.addons}
              canSelectAddons={!product.isFamily}
              className={productPageStyles.addonsMobile}
              isMobile={true}
              smallGutters={true}
              toggleAddon={toggleAddon}
              variantId={variantId}
            />
            <Divider />
            <ProductPrice isMobile {...product} />
            <AddToCartSection
              inDrawer
              fullProductId={fullProductId}
              isFamily={product.isFamily}
              quantity={product.quantity}
              customOptions={customOptions}
              isPurchasable={isPurchasablePure(
                product,
                configurator,
                product.meta,
                shouldDisableBackOrders
              )}
              addItemToCart={handleAdd}
              ticket={product.ticket}
              meta={product.meta as ProductDetailedMeta}
              showDeliveryInfo
              addons={state.addons}
              configuratorVariables={configurator?.values}
            />
          </>
        </ImageAttributeProvider>
      </Section>

      <AccordionContainer>
        <AccordionSection hide={!hasShortDescription} force="open">
          <ProductShortDescription
            brand={getBrandWidget(product)}
            brandUrl={product.brandUrl}
            shortDescription={product.shortDescription}
            summaryAttributes={product.summaryAttributes}
          />
        </AccordionSection>
        <AccordionSection hide={!hasDescription} title={rm('product_description_header')}>
          <ProductDescription
            description={product.description}
            productPdfs={product.productPdfs}
            isTopTested={product.isTopTested}
            efficiencyLabel={product.efficiencyLabel}
            efficiencyLabel2020={product.efficiencyLabel2020}
            pdfClassName={styles.productDescriptionPDF}
          />
        </AccordionSection>
        <AccordionSection hide={!hasAttributes} title={rm('product_description_attributes')}>
          <ProductAttributes
            hideTitle
            className={styles.productAttributes}
            attributes={product.attributes}
          />
        </AccordionSection>
        <AccordionSection
          hide={!hasReviews}
          title={rm('product_review_section_header')}
          right={
            product.reviewSummary && (
              <DrawerReviewStats
                score={product.reviewSummary?.averageScore}
                amountOfReviews={product.reviewSummary.numberOfReviews}
              />
            )
          }
        >
          <ProductReviews
            isMobileLayout={true}
            channel={channel}
            ratingsPerLevel={[]} // not used in mobile version
            productReviews={{ reviews: state.reviews || [], pageNumber: state.reviewsPage || 1 }}
            numberOfReviews={product.reviewSummary?.numberOfReviews || 0}
            loadMoreReviews={loadMoreReviews}
            upvoteReview={upvoteReview}
          />
        </AccordionSection>
      </AccordionContainer>
    </>
  );
};
